/* eslint-disable no-return-await */

import qs from 'qs';
import _ from 'underscore';
import i18next from 'i18next';
import AdminServerActionCreators from '../actions/AdminServerActionCreators';
import AdminConstants from '../constants/AdminConstants';
import AdminAppDispatcher from '../dispatcher/AdminAppDispatcher';
import client from './rest/AdminClient';

const Actions = AdminConstants.ActionTypes;

const nearbyDriversQuery = {
  select: 'first_name last_name phone carrier equipment_class coords available',
  populate: [
    {
      path: 'carrier',
      select: 'name',
    },
  ],
  paginate: true,
  authorized: true,
  per_page: 200,
};

export function getAuthorizedDrivers(id) {
  return client({
    method: 'GET',
    path: `api/1/carriers/${id}/drivers/authorized`,
    params: {
      _: new Date().getTime(),
    },
  });
}

function getAvailableDrivers(props = {}) {
  return client({
    method: 'GET',
    path: `/api/1/${props.id}/drivers/available`,
  });
}

function getNearbyDrivers(id, params = {}, options = {}) {
  const query = _.extend(nearbyDriversQuery, params, options);
  const queryString = qs.stringify(query);

  return client({
    method: 'GET',
    path: `api/v2/drivers/${id}?${queryString}`,
    params: {
      _: new Date().getTime(),
    },
  });
}

function getAllDrivers(id) {
  return client({
    method: 'GET',
    path: `api/1/carriers/${id}/drivers?q=${new Date().toJSON()}`,
  });
}

function toggleDriverInfoBulk({ drivers, carrier_id }) {
  return client({
    method: 'PUT',
    path: `/api/v3/carriers/${carrier_id}/drivers/marketplace/settings`,
    entity: JSON.stringify({ drivers }),
  });
}

function acceptOffer({ offerId, driverId }) {
  return client({
    method: 'PUT',
    path: '/api/1/offers/accept',
    entity: JSON.stringify({
      offer_id: offerId,
      driver_id: driverId,
      accept: true,
      by_proxy: true,
    }),
  });
}

const getDriverById = (id) => {
  return client({
    method: 'GET',
    path: `api/1/drivers/${id}`,
  });
};

const addDriverComment = (id, comment) => {
  return client({
    method: 'POST',
    path: `/admin/api/v2/carriers/${id}/comment`,
    entity: JSON.stringify({ comment }),
  });
};

const DriverWebAPIUtils = {
  getAuthorizedDrivers(id) {
    getAuthorizedDrivers(id).then(
      (response) => {
        AdminServerActionCreators.receiveAuthorizedDriversSuccess(
          response.entity.drivers,
        );
      },
      (response) => {
        console.error(response);
      },
    );
  },
  getAvailableDrivers(props) {
    getAvailableDrivers(props).then(
      (response) => {
        AdminServerActionCreators.receiveAvailableDriversSuccess(
          response.entity.data,
        );
      },
      (response) => {
        console.error(response);
      },
    );
  },
  getNearbyDrivers(id, query) {
    getNearbyDrivers(id, query).then(
      (response) => {
        AdminServerActionCreators.receiveNearbyDriversForCarrierSuccess(
          response.entity.data,
          response.entity.meta.count,
        );
      },
      (response) => {
        console.error(response);
      },
    );
  },
  getAllDrivers(id) {
    getAllDrivers(id).then(
      // get signed urls for profile picture
      (response) =>
        AdminServerActionCreators.receiveDriversSuccess(
          response.entity.drivers,
        ),
      (response) => console.error(response),
    );
  },
  toggleAuthorization({ carrier_id, driver_id, status }) {
    const ids = Array.isArray(driver_id) ? driver_id : [driver_id];
    const drivers = ids.map((driverId) => ({ driverId, authorized: status }));
    toggleDriverInfoBulk({ carrier_id, drivers })
      .then(() =>
        AdminServerActionCreators.toggleDriverAuthorizationSuccess({
          driver_id,
          status,
        }),
      )
      .catch((response) => {
        console.error(response);
        AdminServerActionCreators.toggleDriverAuthorizationFail(
          i18next.t('TOGGLE_DRIVER_AUTHORIZATION_FAIL'),
        );
      });
  },
  toggleReceiveOffers({ carrier_id, driver_id, status }) {
    const ids = Array.isArray(driver_id) ? driver_id : [driver_id];
    const drivers = ids.map((driverId) => ({
      driverId,
      receive_offers: status,
    }));
    toggleDriverInfoBulk({ carrier_id, drivers })
      .then(() =>
        AdminServerActionCreators.toggleReceiveOffersSuccess({
          driver_id,
          status,
        }),
      )
      .catch((response) => {
        console.error(response);
        AdminServerActionCreators.toggleReceiveOffersFail(
          i18next.t('TOGGLE_RECEIVE_OFFERS_FAIL'),
        );
      });
  },
  toggleCanSeePayout({ driver_id, carrier_id, can_see_payout }) {
    const ids = Array.isArray(driver_id) ? driver_id : [driver_id];
    const drivers = ids.map((driverId) => ({ driverId, can_see_payout }));
    toggleDriverInfoBulk({ carrier_id, drivers })
      .then(() =>
        AdminServerActionCreators.toggleCanSeePayoutSuccess({
          driver_id,
          status: can_see_payout,
        }),
      )
      .catch((error) => {
        console.error(error);
        AdminServerActionCreators.toggleCanSeePayoutFail(
          i18next.t('TOGGLE_CAN_SEE_PAYOUT_FAIL'),
        );
      });
  },
  toggleMultiplePickup({ carrier_id, driver_id, status }) {
    const ids = Array.isArray(driver_id) ? driver_id : [driver_id];
    const drivers = ids.map((driverId) => ({
      driverId,
      can_accept_offers_before_pickup: status,
    }));
    toggleDriverInfoBulk({ carrier_id, drivers })
      .then(() =>
        AdminServerActionCreators.toggleMultiplePickupSuccess({
          driver_id,
          status,
        }),
      )
      .catch((response) => {
        AdminServerActionCreators.toggleMultiplePickupFail(
          i18next.t('TOGGLE_MULTIPLE_PICKUP_FAIL'),
        );
        console.error(response);
      });
  },
  acceptOffer(props) {
    acceptOffer(props).then(
      (response) => {
        const {
          entity: { success, error },
        } = response;
        if (success) {
          AdminServerActionCreators.acceptOfferSuccess(props);
        } else {
          AdminServerActionCreators.acceptOfferSuccess({
            ...props,
            error,
          });
        }
      },
      (response) => {
        const {
          entity: { success, error },
        } = response;
        AdminServerActionCreators.acceptOfferFail({
          ...props,
          error,
          success,
        });
      },
    );
  },
  async getDriverById(id) {
    return await getDriverById(id).then(
      (response) => {
        return response.entity;
      },
      (error) => {
        throw error;
      },
    );
  },
  async addDriverComment(id, comment) {
    return await addDriverComment(id, comment).then(
      (response) => {
        const carrier = response.entity.data;
        AdminServerActionCreators.updatedCarrier(carrier._id, carrier);
      },
      (error) => {
        throw error;
      },
    );
  },
};

AdminAppDispatcher.register((payload) => {
  const driverId = payload.action.driverId;
  const offerId = payload.action.offerId;
  switch (payload.action.type) {
    case Actions.ACCEPT_OFFER_REQUEST:
      DriverWebAPIUtils.acceptOffer({ driverId, offerId });
      break;
    default:
  }
});

export default DriverWebAPIUtils;
